import PropTypes from 'prop-types';
import { useEffect } from 'react';
import Router from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { initializeStore } from '@/redux/initialize-store';

import { isUserEmpty } from '@/utils/comparisonUtils';
import { getI18NextConfigForLocale } from '@/utils/locales';
import { sanitizeObject } from '@/utils/millesimaUtils';
import { prefetchLayout } from '@/utils/prefetchLayout';

import Login from '@/components/common/Login';
import { useAppSelector } from '@/redux';
import { NS } from '@/constants';

import styles from './Authentification.module.scss';
import { FOOTER_TYPE } from '@/constants/footer';
import { PAGES } from '@/constants/pages';

const NAMESPACES = [
  NS.COMMON
];

type AuthenticationProps = {
  origin?: string;
  isSignUp: boolean;
}

export const Authentication = ({ origin, isSignUp }: AuthenticationProps): React.JSX.Element => {
  const { t } = useTranslation(NAMESPACES);
  const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);

  useEffect(() => {
    if (!isUserEmpty(loggedUser)) {
      Router.push(origin || PAGES.ACCOUNT);
    }
  }, [loggedUser, origin]);

  return (
    <div className={`${styles.container} new-style tu-auth-page`} data-test-id="auth-page">
      <div className="container">
        <Login t={t} isSignUp={isSignUp} />
      </div>
    </div>
  );
};

Authentication.propTypes = {
  loggedUser: PropTypes.object,
  origin: PropTypes.string,
  isSignUp: PropTypes.bool.isRequired
};

export default Authentication;

export const getServerSideProps = (async (ctx) => {
  const store = initializeStore({ ssrContext: ctx });
  const layoutOptions = await prefetchLayout(ctx, store);
  const { locale } = store.getState()?.root.storeConf || ctx;
  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));
  const { query } = ctx;

  return {
    props: sanitizeObject({
      ...translate,
      layoutOptions: {
        ...layoutOptions,
        footerType: FOOTER_TYPE.AUTHENTICATION
      },
      isSignUp: !!query.isSignUp,
      origin: query.origin,
      initialReduxState: store.getState()
    })
  };
});
